import {
    HasKids,
    type KidInfo,
    type MembershipProfileAnswerUpsertDto,
    type MembershipProfileKidAnswer,
    QuestionKey,
} from '@fruitful/member-profile';
import { zodResolver } from '@hookform/resolvers/zod';
import { router } from 'expo-router';
import { useMemo } from 'react';
import { type SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import {
    BodyLargeRegular,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CloseButton,
    ControlledInput,
    Form,
    FullScreenSpinner,
    PlusIcon,
    ScreenContainer,
    USE_FORM_MODE_DEFAULTS,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { useViewEvents } from '../../events/hooks/useViewEvents';
import { captureException } from '../../logger/Logger';
import useGetMembershipProfileAnswers from '../hooks/useGetMembershipProfileAnswers';
import { useTrackFinancialProfileCompletion } from '../hooks/useTrackFinancialProfileCompletion';
import useUpsertMembershipProfileAnswer from '../hooks/useUpsertMembershipProfileAnswer';
import {
    transformDataToMembershipProfileAnswerUpsertDto,
    transformMembershipProfileAnswerToUpsertDto,
    transformUserToMemberProfileMembership,
} from '../utils/memberProfileUtils';
import {
    SUCCESS_TOAST_DURATION,
    SUCCESS_TOAST_MESSAGE,
    SuccessToastIcon,
} from './SuccessToast';

const kidSchema = z.object({
    firstName: z.string().nullish(),
    birthYear: z.string().regex(/^\d{4}$/u, 'Enter a 4-digit year'),
});

const kidsInfoSchema = z.object({
    kids: z.array(kidSchema).min(1, 'Add at least one child'),
});

type KidsInfoSchema = z.infer<typeof kidsInfoSchema>;

export const KidsInfo = () => {
    const { trackViewOnce } = useViewEvents();
    trackViewOnce('Profile/KidsInfo');

    const toast = useToastController();
    const user = useAuthedUser();
    const membership = transformUserToMemberProfileMembership(user);

    const { trackFinancialProfileUpdate } =
        useTrackFinancialProfileCompletion();

    const {
        data: kids,
        isLoading: isLoadingProfileAnswers,
        refetch: refetchKidsAnswer,
    } = useGetMembershipProfileAnswers<MembershipProfileKidAnswer>({
        questionKey: QuestionKey.KIDS,
    });

    const { mutateAsync: upsertMembershipProfileAnswer } =
        useUpsertMembershipProfileAnswer();

    const existingData = useMemo(() => {
        return kids != null
            ? transformMembershipProfileAnswerToUpsertDto<MembershipProfileKidAnswer>(
                  kids,
              )
            : undefined;
    }, [kids]);

    const defaultKids: KidInfo[] = useMemo(() => {
        return existingData?.answer.additionalInformation ?? [];
    }, [existingData]);

    const {
        control,
        handleSubmit,
        formState: { isValid, errors },
        register,
    } = useForm<KidsInfoSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(kidsInfoSchema),
        defaultValues: {
            kids:
                defaultKids.length > 0
                    ? defaultKids
                    : [{ firstName: '', birthYear: '' }],
        },
    });

    const { fields, append, remove } = useFieldArray({ control, name: 'kids' });

    const onSubmit: SubmitHandler<KidsInfoSchema> = async ({
        kids: submittedKids,
    }) => {
        try {
            const currentAnswer = HasKids.YES; // If on the KidsInfo page, the answer to HasKids will always be Yes.
            const normalizedKids: KidInfo[] = submittedKids.map((k) =>
                k.firstName == null || k.firstName === ''
                    ? { birthYear: k.birthYear }
                    : { birthYear: k.birthYear, firstName: k.firstName },
            );
            const dto: MembershipProfileAnswerUpsertDto<MembershipProfileKidAnswer> =
                transformDataToMembershipProfileAnswerUpsertDto<MembershipProfileKidAnswer>(
                    {
                        answer: currentAnswer,
                        additionalInformation: normalizedKids,
                    },
                    QuestionKey.KIDS,
                    membership,
                    user,
                );

            await upsertMembershipProfileAnswer({ body: [dto] });

            toast.show(SUCCESS_TOAST_MESSAGE, {
                duration: SUCCESS_TOAST_DURATION,
                icon: SuccessToastIcon,
            });

            trackFinancialProfileUpdate({ kidsUpdated: kids != null });

            void refetchKidsAnswer();
            router.navigate('/profile/financial-profile');
        } catch (exception) {
            captureException(exception);
            toast.show('Something went wrong!', { duration: 2000 });
        }
    };

    if (isLoadingProfileAnswers) {
        return <FullScreenSpinner />;
    }

    return (
        <ScreenContainer title="Kids" hasBackButton={true}>
            <YStack gap={24} justifyContent="space-between">
                <YStack gap={16}>
                    <BodyLargeRegular>How old are your kids?</BodyLargeRegular>
                    <BodyMediumRegular color="$gray1000">
                        Insights into any kids helps your Guide to prioritize
                        funds going forward and to understand your full expense
                        picture.
                    </BodyMediumRegular>

                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <YStack gap={12}>
                            {fields.map((field, index) => (
                                <XStack
                                    key={field.id}
                                    gap={12}
                                    width="100%"
                                    justifyContent="space-evenly"
                                >
                                    <View flex={1}>
                                        <ControlledInput
                                            key={field.id}
                                            {...register(
                                                `kids.${index}.firstName`,
                                            )}
                                            label="First name (optional)"
                                            control={control}
                                            register={register}
                                            errors={errors}
                                            autoCapitalize="words"
                                            autoComplete="name-given"
                                            textContentType="name"
                                        />
                                    </View>
                                    <View flex={1}>
                                        <ControlledInput
                                            key={field.id}
                                            {...register(
                                                `kids.${index}.birthYear`,
                                            )}
                                            label="Birth year"
                                            placeholder="YYYY"
                                            control={control}
                                            register={register}
                                            errors={errors}
                                            autoComplete="birthdate-year"
                                            type="numeric"
                                            maxLength={4}
                                            inputMode="numeric"
                                        />
                                    </View>
                                    <View
                                        alignSelf="center"
                                        width={40}
                                        marginTop={20}
                                    >
                                        {index !== 0 && (
                                            <CloseButton
                                                onPress={() => {
                                                    remove(index);
                                                }}
                                            />
                                        )}
                                    </View>
                                </XStack>
                            ))}

                            <Button
                                size={ButtonSize.LARGE}
                                variant={ButtonVariant.SECONDARY}
                                text="Add kid"
                                icon={PlusIcon}
                                iconPosition="left"
                                onPress={() => {
                                    append({
                                        firstName: '',
                                        birthYear: '',
                                    } satisfies KidInfo);
                                }}
                            />
                        </YStack>
                    </Form>
                </YStack>

                <Button
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    text="Save"
                    onPress={handleSubmit(onSubmit)}
                    disabled={!isValid}
                />
            </YStack>
        </ScreenContainer>
    );
};

export default KidsInfo;
